import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  Textarea,
  useRadio,
  VisuallyHidden,
  cn,
  Spinner,
  ModalFooter,
  ModalHeader,
  Chip,
  Tooltip,
  Select,
  SelectItem,
  // Removed Text from import
  Accordion,
  AccordionItem,
  DatePicker,
  Skeleton,
} from '@nextui-org/react'

import { today, parseDate, getLocalTimeZone } from '@internationalized/date'

import { useWorkspaceContextData } from 'src/context/workspace'
import { useSocketContextData } from 'src/context/socket'
import { useNavigate, useParams } from 'react-router-dom'
import CreatableSelect from 'react-select/creatable'
import { useSubscriptionContext } from 'src/context/subscription'
import Mixpanel from 'src/lib/mixpanel'
import { Icon } from '@iconify/react'
import { emitter } from 'src/lib/EventEmitter'
import { useDropzone } from 'react-dropzone'
import api from 'src/lib/api'
import { PowerDocsEventsName } from 'src/types/SocketEvents'

const inActiveButtonClass = 'bg-transparent text-default-400 pl-5 pr-5'
const activeButtonClass = 'text-black-500 font-medium bg-white rounded-md  pl-5 pr-5'

// StepperIndicator removed

// ------------------ CustomRadio ------------------ //
export const CustomRadio = (props) => {
  const {
    Component,
    children,
    isSelected,
    description,
    getBaseProps,
    getWrapperProps,
    getInputProps,
    getLabelProps,
    getLabelWrapperProps,
    getControlProps,
  } = useRadio(props)

  return (
    <Component
      {...getBaseProps()}
      className={cn(
        'group inline-flex items-center hover:opacity-70 active:opacity-50 justify-between flex-row-reverse tap-highlight-transparent',
        'max-w-[300px] cursor-pointer border-2 border-default rounded-lg gap-4 p-2',
        'data-[selected=true]:border-secondary',
      )}
    >
      <VisuallyHidden>
        <input {...getInputProps()} />
      </VisuallyHidden>
      <span {...getWrapperProps()} style={isSelected ? { borderColor: '#65d0ca' } : {}}>
        <span {...getControlProps()} style={isSelected ? { backgroundColor: '#65d0ca' } : {}} />
      </span>
      <div {...getLabelWrapperProps()}>
        {children && <span {...getLabelProps()}>{children}</span>}
        {description && <span className="text-small text-foreground opacity-70">{description}</span>}
      </div>
    </Component>
  )
}

// ------------------ STEP 1: Document Type ------------------ //
function StepDocumentType({ formData, setFormData, setOpenAccordion }) {
  const handleSelectDocumentType = (type: string, subType?: string) => {
    setFormData((prev: any) => ({ ...prev, documentType: type, subType }))
    // setOpenAccordion(2);
  }
  const documentsTypes = useMemo(
    () => [
      {
        title: 'Proposal → Tender',
        description: 'Bid for public contracts or services by responding to an RFP.',
        documentType: 'PROPOSAL',
        subType: 'Tender Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Proposal → Sales',
        description: 'Convince a client to purchase a product or service.',
        documentType: 'PROPOSAL',
        subType: 'Sales Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Proposal → Project',
        description: 'Present a structured plan to execute a specific project.',
        documentType: 'PROPOSAL',
        subType: 'Project Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Proposal → Marketing',
        description: 'Outline strategies to promote a product or service.',
        documentType: 'PROPOSAL',
        subType: 'Marketing Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Offer',
        description: 'Formalize terms and conditions to initiate a business deal.',
        documentType: 'OFFER',
        subType: '',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Quotation',
        description: 'Specify pricing and conditions for goods or services.',
        documentType: 'QUOTATION',
        subType: '',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Contract',
        description: 'Establish binding terms for obligations and responsibilities.',
        documentType: 'CONTRACT',
        subType: '',
        available: false,
        icon: 'lucide:file-text',
      },
      {
        title: 'NDA',
        description: 'Secure confidential information through legal protection.',
        documentType: 'NDA',
        subType: '',
        available: false,
        icon: 'lucide:file-text',
      },
      // {
      //   title: 'Invoice',
      //   description: 'A request for payment after work is completed.',
      //   documentType: 'INVOICE',
      //   subType: '',
      //   available: false,
      //   icon: 'lucide:file-text',
      // },
    ],
    [],
  )
  return (
    <div className="flex flex-col gap-8 p-2">
      <div className="grid grid-cols-4 gap-4">
        {documentsTypes.map((documentOption) => (
          <div
            key={documentOption.title}
            className={`flex flex-col gap-3 border-3 p-4 py-8 rounded-lg bg-default-200 ${
              documentOption.available ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
            } ${
              formData.documentType === documentOption.documentType && formData.subType === documentOption.subType
                ? 'border-secondary'
                : 'border-default-200'
            }`}
            onClick={() =>
              documentOption.available
                ? handleSelectDocumentType(documentOption.documentType, documentOption.subType)
                : ''
            }
          >
            {documentOption.available ? (
              <div className="border rounded-xl bg-default-100 border-none p-3 w-fit">
                <Icon icon={documentOption.icon} width="20" />
              </div>
            ) : (
              <div className="flex justify-between">
                <div className="border rounded-xl bg-default-100 border-none p-3 w-fit">
                  <Icon icon="lucide:file-lock" width="20" />
                </div>
                <Chip color="secondary">Coming soon</Chip>
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold">{documentOption.title}</h3>
              <p className="text-sm text-default-500">{documentOption.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ------------------ STEP 2: Project Info ------------------ //
function StepProjectInfo({
  formData,
  setFormData,
  clientsFullList,
  selectedClient,
  setSelectedClient,
  loadScope,
  setNextButtonStatus,
  updateDocumentData,
  setLanguage,
  isVisible,
  closeModal,
}: {
  formData: any
  setFormData: (value: any) => void
  clientsFullList: any
  selectedClient: any
  setSelectedClient: (value: any) => void
  loadScope: () => void
  setNextButtonStatus: (value: boolean) => void
  updateDocumentData: (key: string, value: any) => void
  setLanguage: (lang: string) => void
  isVisible: boolean
  closeModal: () => void
}) {
  // Add touched state locally to this component
  const [touched, setTouched] = useState({ title: false, clientName: false })
  const [errors, setErrors] = useState({ title: false, clientName: false })
  // --- Scraping state and logic from OnboardingWizard ---
  const [scraping, setScraping] = useState(false)
  const { io } = useSocketContextData()
  // --- Track if title was manually set ---
  const [isTitleManuallySet, setIsTitleManuallySet] = useState(false)
  const scrapeClientWebsite = (website: string) => {
    if (!io || !website || scraping) return
    setScraping(true)
    io.emit('getCompanyProfile', { url: website }, (response) => {
      if (response?.name && !isTitleManuallySet) {
        const currentDate = new Date().toISOString().split('T')[0]
        setFormData((prev: any) => ({
          ...prev,
          clientName: response.name,
          title: `${prev.subType ? prev.subType : ''} for ${response.name}`.trim(),
          date: currentDate,
        }))
        setSelectedClient({ value: null, label: response.name })
      }
      setScraping(false)
    })
  }

  useEffect(() => {
    const isTitleValid = formData?.title?.trim().length > 0
    const isClientNameValid = formData?.clientName?.trim().length > 0

    setErrors({
      title: touched.title && !isTitleValid,
      clientName: touched.clientName && !isClientNameValid,
    })

    setNextButtonStatus(isTitleValid && isClientNameValid)
  }, [formData?.title, formData?.clientName, touched])

  const [progress, setProgress] = useState(0)
  const [variant, setVariant] = useState<'danger' | 'warning' | 'secondary'>('danger')
  const [uploading, setUpload] = useState<boolean>(false)
  const [training, setTraining] = useState<boolean>(false)
  const [reseting, setReseting] = useState<boolean>(false)
  const [filesDescription, setFilesDescription] = useState<string>('')
  const uploadDiv = useRef<HTMLDivElement>(null)
  const trainingDiv = useRef<HTMLDivElement>(null)
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setFormData((prev: any) => ({
      ...prev,
      filesToUpload: acceptedFiles,
    }))
    trainingDiv.current?.classList.add('hidden')
    uploadDiv.current?.classList.remove('hidden')
    setUpload(true)
    const formData = new FormData()
    acceptedFiles.map((file: File) => formData.append('files', file))
    const res = await api.post(`/urs/files`, formData)
    setUpload(false)
    setFormData((prev: any) => ({
      ...prev,
      filesToUpload: [],
    }))
    if (res.data?.data?.length > 0) {
      setFormData((prev: any) => ({
        ...prev,
        files: [...prev.files, ...res.data.data],
      }))
    }
  }, [])
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxFiles: 5 - formData.files?.length,
  })
  const resetSettings = () => {
    setFormData((prev: any) => ({
      ...prev,
      filesDescription: '',
      files: [],
      filesToUpload: [],
    }))
  }
  const trainData = async () => {
    uploadDiv.current?.classList.add('hidden')
    trainingDiv.current?.classList.remove('hidden')
    setNextButtonStatus(false)
    setTraining(true)
    const response = await io.emitWithAck('extractDescription', {
      files: formData.files,
      token: localStorage.getItem('accessToken'),
    })
    // Process AI response for structured data
    if (response) {
      console.log(response)
      // Handle Client name
      if (response['Client name']) {
        setFormData((prev) => ({ ...prev, clientName: response['Client name'] }))
        setSelectedClient({ label: response['Client name'], value: null })
      }
      // Handle Project description
      if (response['Project description']) {
        setFormData((prev) => ({ ...prev, projectDescription: response['Project description'] }))
        setFilesDescription(response['Project description'])
      }
      // Handle Scope of work
      if (Array.isArray(response['Scope of work']) && response['Scope of work'].length > 0) {
        setFormData((prev) => ({
          ...prev,
          scopeOfProject: Array.isArray(response['Scope of work']) ? response['Scope of work'] : [],
        }))
        console.log(
          'Updated scope of work: ',
          Array.isArray(response['Scope of work']) ? response['Scope of work'] : [],
        )
      }
      // Handle Client website
      if (response['Client website']) {
        setFormData((prev) => ({ ...prev, clientWebsite: response['Client website'] }))
      }
      // Handle Client industry
      if (response['Client industry']) {
        setFormData((prev) => ({ ...prev, clientIndustry: response['Client industry'] }))
      }
      // Handle Client communication style
      if (response['Client communication style']) {
        setFormData((prev) => ({ ...prev, clientCommunicationStyle: response['Client communication style'] }))
      }
      // Handle Client country
      if (response['Client country']) {
        setFormData((prev) => ({ ...prev, clientCountry: response['Client country'] }))
      }
      // Handle Tender Reference Number
      if (response['Tender Reference Number']) {
        setFormData((prev) => ({ ...prev, tenderReferenceNumber: response['Tender Reference Number'] }))
      }
      // Handle Compliance Requirements
      if (response['Compliance Requirements']) {
        setFormData((prev) => ({ ...prev, complianceRequirements: response['Compliance Requirements'] }))
      }
      // Handle Risk management
      if (response['Risk management']) {
        setFormData((prev) => ({ ...prev, riskManagement: response['Risk management'] }))
      }
      // Handle Cost breakdown
      if (response['Cost breakdown']) {
        setFormData((prev) => ({ ...prev, costBreakdown: response['Cost breakdown'] }))
      }
      // Handle Budget estimation
      if (response['Budget estimation']) {
        setFormData((prev) => ({ ...prev, budgetEstimation: response['Budget estimation'] }))
      }
      // Update language selection in AI Settings based on "Primary document language"
      if (
        response?.['Primary document language'] &&
        ['English', 'English (UK)', 'Spanish', 'French', 'German', 'Arabic', 'Japanese'].includes(
          response['Primary document language'],
        )
      ) {
        setLanguage(response['Primary document language'])
      } else {
        setLanguage('English')
      }
      // Always update files (and title if possible)
      setFormData((prev: any) => ({
        ...prev,
        files: formData.files,
        filesDescription: response['Project description'] || prev.filesDescription,
        title:
          response['Client name'] && prev.subType
            ? `${prev.subType} for ${response['Client name']}`.trim()
            : prev.title,
      }))
    }
    setTraining(false)
    setNextButtonStatus(true)
  }
  useEffect(() => {
    const words = formData?.description?.split(' ').length - 1 || 0
    const newProgress = Math.min((words / 20) * 100, 100)
    setProgress(newProgress)
    if (newProgress < 50) setVariant('danger')
    else if (newProgress < 90) setVariant('warning')
    else setVariant('secondary')
  }, [formData?.description])

  return isVisible && (
    <div className="flex flex-col gap-10 p-0">
      {/* Project brief: File input */}
      <div className="flex flex-col gap-4 border-1 border-black rounded-xl p-5">
        <div className="flex flex-col">
          <span className={'flex flex-row text-md font-semibold items-center gap-2'}>
            What documents describe this project?
          </span>
          <span className={'text-sm'}>
            Upload relevant files so we can extract project details and generate a concise brief for you.
          </span>
        </div>
        <div
          {...getRootProps({
            className:
              'flex flex-col gap-2 mt-2 bg-default-100 rounded-xl border-2 border-dashed border-default-400 justify-center items-center cursor-pointer',
          })}
        >
          <input {...getInputProps()} />
          {[...formData.files, ...formData.filesToUpload].length > 0 ? (
            <div className={'flex flex-col justify-center items-center gap-4 p-4 w-full'}>
              <div className={'grid grid-cols-2 gap-4 w-full'}>
                {[...formData.files, ...formData.filesToUpload].map((file: File | { fileName: string }) => {
                  return (
                    <div
                      className={'bg-white flex rounded flex-row p-2'}
                      key={`file_${(file as File).name ?? (file as any).fileName}`}
                    >
                      <img src={'/assets/icons/file-pdf.png'} width={40} height={50} />
                      <div className={''}>
                        <p className={'text-xs'}>{(file as File).name ?? (file as any).fileName}</p>
                        <p className={'text-xs'}>PDF File</p>
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className={'flex w-full justify-center items-center gap-1 hidden'} ref={uploadDiv}>
                <div className={'flex flex-1 h-[1px] bg-black opacity-20'} />
                <div
                  className={
                    'pt-1 pb-1 pr-6 pl-6 rounded-2xl top-[-4px] bg-warning-400 items-center justify-center flex gap-2 border-2 border-white'
                  }
                >
                  {uploading && <Spinner size={'sm'} color={'white'} />}
                  <span className={'text-xs text-white'}>{uploading ? 'Uploading files' : 'Document Uploaded'}</span>
                </div>
                <div className={'flex flex-1 h-[1px] bg-black opacity-20'} />
              </div>
              <div className={'flex w-full justify-center items-center gap-1 hidden'} ref={uploadDiv}>
                <div className={'flex flex-1 h-[1px] bg-black opacity-20'} />
                <div
                  className={
                    'pt-1 pb-1 pr-6 pl-6 rounded-2xl top-[-4px] bg-warning-400 items-center justify-center flex gap-2 border-2 border-white'
                  }
                >
                  {uploadDiv && <Spinner size={'sm'} color={'white'} />}
                  <span className={'text-xs text-white'}>{uploading ? 'Uploading' : 'Document Uploaded'}</span>
                </div>
                <div className={'flex flex-1 h-[1px] bg-black opacity-20'} />
              </div>
              <div className={'flex w-full justify-center items-center gap-1 hidden'} ref={trainingDiv}>
                <div className={'flex flex-1 h-[1px] bg-black opacity-20'} />
                <div
                  className={
                    'pt-1 pb-1 pr-6 pl-6 rounded-2xl top-[-4px] bg-warning-400 items-center justify-center flex gap-2 border-2 border-white'
                  }
                >
                  {training && <Spinner size={'sm'} color={'white'} />}
                  <span className={'text-xs text-white'}>{training ? 'Processing your files' : 'File processed'}</span>
                </div>
                <div className={'flex flex-1 h-[1px] bg-black opacity-20'} />
              </div>
              <div className={'flex flex-row flex-1 justify-between items-center p-4 w-full pt-0'}>
                <Button
                  variant={'bordered'}
                  color={'default'}
                  style={{ borderColor: '#1c1c1c' }}
                  isDisabled={uploading || formData.files?.length === 5 || reseting}
                  onClick={() => {
                    open()
                  }}
                >
                  <Icon icon={'lucide:plus'} color={'#1c1c1c'} />
                  <span>Upload file</span>
                </Button>
                <div className={'flex flex-row gap-4'}>
                  <Button
                    variant={'bordered'}
                    color={'default'}
                    style={{ borderColor: '#1c1c1c' }}
                    isDisabled={uploading || reseting}
                    onClick={resetSettings}
                  >
                    {reseting && <Spinner size={'sm'} />}
                    <span>{reseting ? 'Resetting' : 'Remove all Files'}</span>
                  </Button>
                  <Button
                    variant={'solid'}
                    color={'primary'}
                    style={{ borderColor: '#1c1c1c' }}
                    isDisabled={uploading || reseting || training}
                    onClick={trainData}
                  >
                    <span>Scan & Process now</span>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className={'flex flex-col justify-center items-center p-4'}>
              <div className={'bg-white p-2 rounded-full mb-2'}>
                <Icon icon={'lucide:cloud-upload'} />
              </div>
              <p className={'font-regular text-sm'}>
                <span className={'pr-1 font-medium text-blue-400'} onClick={open}>
                  Click or drag files to upload
                </span>
                <span className={'font-light text-blue-400 '}>(max 5 files)</span>
              </p>
              <span className={'text-xs font-regular'}>PDF or DOCX (max. 5 MB per file)</span>
            </div>
          )}
        </div>
      </div>

      {/* Project brief: Manual input */}
      <div className="flex flex-col gap-4 border-1 border-black rounded-xl p-5">
        <div className="flex flex-col">
          <span className={'flex flex-row text-md font-semibold items-center gap-2'}>
            What's the client information?
          </span>
          <span className={'text-sm'}>Provide or update client details to ensure accurate document creation.</span>
        </div>
        <div className="flex flex-col gap-6">
          {/* Client Website input with Scrape button */}
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <label className="text-sm pb-1 block">Client website</label>
              <div className="flex gap-2 items-center">
                <Input
                  variant="bordered"
                  placeholder="e.g. www.acme-inc.com"
                  classNames={{ label: 'text-secondary'}}
                  value={formData?.clientWebsite ?? ''}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, clientWebsite: e.target.value }))
                    updateDocumentData('clientWebsite', e.target.value)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      scrapeClientWebsite(formData.clientWebsite)
                    }
                    if (e.key === 'Escape') {
                      closeModal?.()
                    }
                  }}
                  autoFocus={isVisible}
                  endContent={
                    <Button
                      className="px-8"
                      variant="light"
                      color="secondary"
                      isDisabled={scraping}
                      isLoading={scraping}
                      onPress={() => scrapeClientWebsite(formData.clientWebsite)}
                    >
                      Get data from website
                    </Button>
                  }
                />
              </div>
            </div>
            {/* Client Name input */}
            <div className="flex-1">
              <label className="text-sm pb-1 block">
                Client name <span className="text-red-500">*</span>
              </label>
              <CreatableSelect
                options={clientsFullList.map((item: any) => ({
                  value: item.uuid,
                  label: item.name,
                }))}
                placeholder="Select or create new"
                value={selectedClient}
                onChange={(value) => {
                  setFormData((prev: any) => ({
                    ...prev,
                    clientName: value?.label,
                  }))
                  setSelectedClient(value)
                  setTouched((prev) => ({ ...prev, clientName: true }))
                  updateDocumentData('clientName', value?.label || '')
                }}
                onCreateOption={(inputValue: string) => {
                  setFormData((prev: any) => ({
                    ...prev,
                    clientName: inputValue,
                  }))
                  setSelectedClient({ label: inputValue, value: null })
                  setTouched((prev) => ({ ...prev, clientName: true }))
                  updateDocumentData('clientName', inputValue)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    closeModal?.()
                  }
                }}
                formatCreateLabel={(inputValue: string) => (
                  <div className="flex justify-between items-center">
                    New client: {inputValue}
                    <span className="z-0 group relative inline-flex items-center justify-center box-border appearance-none select-none whitespace-nowrap font-normal subpixel-antialiased overflow-hidden tap-highlight-transparent data-[pressed=true]:scale-[0.97] outline-none data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2 px-4 min-w-20 h-10 text-small gap-2 rounded-medium [&>svg]:max-w-[theme(spacing.8)] transition-transform-colors-opacity motion-reduce:transition-none bg-secondary text-secondary-foreground data-[hover=true]:opacity-hover">
                      <Icon icon="lucide:plus" />
                      Create client
                    </span>
                  </div>
                )}
                menuPosition="fixed"
                menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
                styles={{
                  container: (baseStyles) => ({
                    ...baseStyles,
                    minWidth: '100%',
                    borderColor: errors.clientName ? '#f31260' : '#e5e7eb',
                    borderWidth: 2,
                    borderRadius: 8,
                    padding: 0,
                    height: '42px',
                    boxShadow: 'none',
                  }),
                  control: (baseStyles) => ({
                    ...baseStyles,
                    border: 'none',
                    height: '100%',
                    width: '100%',
                    outline: 'none',
                    boxShadow: 'none',
                    cursor: 'pointer',
                  }),
                  menuPortal: (defaultStyles) => ({
                    ...defaultStyles,
                    zIndex: 99999,
                  }),
                  option: (styles, state) => ({
                    ...styles,
                    cursor: 'pointer',
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: '0', // Remove extra spacing
                    padding: '0', // Adjust padding to fit the height
                  }),
                }}
              />
              {errors.clientName && <span className="text-red-500 text-xs">Client name is required</span>}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4 border-1 border-black rounded-xl p-5">
        <div className="flex flex-col">
          <span className={'flex flex-row text-md font-semibold items-center gap-2'}>
            What are the project details?
          </span>
          <span className={'text-sm'}>Specify project goals and details to guide document generation.</span>
        </div>
        <div className="flex flex-col gap-6">
          {/* Project Title input + Project Date Picker */}
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <Input
                isRequired
                label="Project title"
                labelPlacement="outside"
                variant="bordered"
                placeholder="e.g. Redesign of running app 'Explorun'"
                classNames={{ label: 'text-secondary' }}
                value={formData?.title ?? ''}
                onChange={(e) => {
                  const value = e.target.value
                  setFormData((prev: any) => ({ ...prev, title: value }))
                  setTouched((prev) => ({ ...prev, title: true }))
                  setIsTitleManuallySet(true)
                  updateDocumentData('title', value)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    closeModal?.()
                  }
                }}
                isInvalid={errors.title}
                errorMessage={errors.title ? 'Project title is required' : ''}
              />
            </div>
            <div className="flex-1">
              <DatePicker
                label="Project creation date"
                labelPlacement="outside"
                hideTimeZone
                granularity="day"
                showMonthAndYearPickers
                variant="bordered"
                placeholder="Select date"
                size="md"
                defaultValue={today(getLocalTimeZone())}
                onChange={(value) => {
                  const formattedDate = parseDate(value.toString()).toString()
                  setFormData((prev) => ({
                    ...prev,
                    projectCreationDate: formattedDate,
                  }))
                  updateDocumentData('projectDate', formattedDate)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    closeModal?.()
                  }
                }}
              />
            </div>
            {/* Project Objective Dropdown for Proposal */}
            {formData.documentType === 'PROPOSAL' && (
              <div className="flex-1">
                <Select
                  label="Project objective"
                  labelPlacement="outside"
                  placeholder="Select Project Objectives"
                  selectedKeys={formData.projectObjectives ? new Set(formData.projectObjectives) : new Set(['none'])}
                  onSelectionChange={(keys) => {
                    const values = Array.from(keys)
                    setFormData((prev: any) => ({ ...prev, projectObjectives: values }))
                  }}
                  variant="bordered"
                  size="md"
                  className="w-full"
                >
                  <SelectItem key="none">No specific objectives</SelectItem>
                  {formData.subType === 'Sales Proposal' && (
                    <>
                      <SelectItem key="sell_product_or_service">Sell product/service</SelectItem>
                      <SelectItem key="convert_lead">Convert lead</SelectItem>
                      <SelectItem key="upsell_client">Upsell existing client</SelectItem>
                      <SelectItem key="demonstrate_value">Demonstrate product value</SelectItem>
                      <SelectItem key="secure_long_term_contract">Secure long-term contract</SelectItem>
                    </>
                  )}
                  {formData.subType === 'Tender Proposal' && (
                    <>
                      <SelectItem key="secure_public_contract">Secure a public contract</SelectItem>
                      <SelectItem key="win_private_bid">Win private bid</SelectItem>
                      <SelectItem key="comply_with_regulations">Comply with regulatory requirements</SelectItem>
                      <SelectItem key="achieve_high_score">Achieve high evaluation score</SelectItem>
                      <SelectItem key="provide_best_value">Provide best value for public funds</SelectItem>
                    </>
                  )}
                  {formData.subType === 'Project Proposal' && (
                    <>
                      <SelectItem key="obtain_project_approval">Obtain project approval</SelectItem>
                      <SelectItem key="demonstrate_feasibility">Demonstrate feasibility</SelectItem>
                      <SelectItem key="align_stakeholders">Align stakeholders</SelectItem>
                      <SelectItem key="outline_budget">Outline budget and timeline</SelectItem>
                      <SelectItem key="risk_assessment">Provide risk assessment</SelectItem>
                    </>
                  )}
                  {formData.subType === 'Marketing Proposal' && (
                    <>
                      <SelectItem key="raise_brand_awareness">Raise brand awareness</SelectItem>
                      <SelectItem key="launch_campaign">Launch new campaign</SelectItem>
                      <SelectItem key="engage_target_audience">Engage target audience</SelectItem>
                      <SelectItem key="boost_lead_generation">Boost lead generation</SelectItem>
                      <SelectItem key="position_product">Position product in the market</SelectItem>
                    </>
                  )}
                  {formData.subType === 'Custom' && (
                    <>
                      <SelectItem key="user_defined_purpose">User-defined purpose</SelectItem>
                      <SelectItem key="specific_problem_solution">Specific problem solution</SelectItem>
                      <SelectItem key="pilot_project">Pilot project proposal</SelectItem>
                      <SelectItem key="innovation_pitch">Innovation pitch</SelectItem>
                      <SelectItem key="community_initiative">Community initiative</SelectItem>
                    </>
                  )}
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ------------------ STEP 3: Project Scope & Generate ------------------ //
function StepProjectScope({
  formData,
  setFormData,
  scopes,
  scopesLoading,
  showAdvancedOption,
  setShowAdvancedOption,
  defaultDocLength,
  setDefaultDocLength,
  formality,
  setFormality,
  modeOfAddress,
  setModeOfAddress,
  audianceType,
  setAudianceType,
  language,
  setLanguage,
  fineTuneText,
  setFineTuneText,
  loading,
  setIsAdvancedSettingsOpen,
  errors,
  setErrors,
  progress,
  setProgress,
  touched,
  setTouched,
  loadScope,
  filesDescription,
  setFilesDescription,
  variant,
  setVariant,
  productOffering,
  setProductOffering,
  updateDocumentData,
  setNextButtonStatus,
  outOfContextError,
  setOutOfContextError,
  isVisible,
  closeModal,
}) {
  // Update touched state initialization to only description
  const [touchedState, setTouchedState] = useState({ description: false })
  // Tender Proposal extra state handlers (add to parent state in main component in a real app)
  // But for now, use formData for all, and helper setters for local fields:
  const handleTenderInput = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }
  // For issuing authority options
  const issuingAuthorities = ['Government', 'Private Sector', 'International Organization', 'Non-Profit']
  const tenderSubmissionFormats = ['Digital PDF', 'Printed Copy', 'Online Form', 'Other']
  // Conditional rendering based on proposal type
  const isSalesProposal = formData?.subType === 'Sales Proposal'
  const isTenderProposal = formData?.subType === 'Tender Proposal'
  const isProjectProposal = formData?.subType === 'Project Proposal'
  const isMarketingProposal = formData?.subType === 'Marketing Proposal'
  // Default: show all for sales; show reduced + tender fields for tender
  const descriptionError =
    outOfContextError ||
    (touchedState?.description && errors?.description ? 'Project description is required' : '')

  return (
    <div className="flex flex-col gap-10 p-0">
      <div className="flex flex-col gap-4 border-1 border-black rounded-xl p-5">
        <div className="flex flex-col">
          <span className={'flex flex-row text-md font-semibold items-center gap-2'}>What is the project about?</span>
          <span className={'text-sm'}>Describe your project so we can understand its context and requirements.</span>
        </div>
        <div className="flex flex-col gap-6">
          <Textarea
            minRows={4}
            maxRows={6}
            variant="bordered"
            labelPlacement="outside"
            label="Project Description & Scope"
            value={formData.projectDescription}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, projectDescription: e.target.value }))
              updateDocumentData('description', e.target.value)
              setTouchedState((prev) => ({ ...prev, description: true }))
              if (outOfContextError) setOutOfContextError(null)
              // setErrors((prev) => ({ ...prev, description: !e.target.value.trim() }))
            }}
            onBlur={(e) => {
              setTouchedState((prev) => ({ ...prev, description: true }))
              loadScope(e.target.value)
            }}
            className="mt-1 block w-full"
            placeholder="e.g. Supply and install 200 solar panels for public school"
            description="Summarize the project in one sentence (the more details the better)"
            rows={4}
            isInvalid={!!descriptionError}
            errorMessage={descriptionError}
            autoFocus={isVisible}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                closeModal?.()
              }
            }}
          />
        </div>

        <div className="flex flex-col gap-4 mt-4">
          <div className="flex flex-col">
            <span className={'flex flex-row text-md font-semibold items-center gap-2'}>
              What's the project's scope?
            </span>
            <span className={'text-sm'}>
              This is the scope of work to be completed. Keep only those that your company can deliver.
            </span>
          </div>
        </div>

        {formData.scopeOfProject.length === 0 && (
          <Skeleton className="rounded-lg">
            <div className="h-24 rounded-lg bg-default-300" />
          </Skeleton>
        )}

        {(scopesLoading || (formData.scopeOfProject.length > 0 && !outOfContextError)) && (
          <div className="flex flex-col">
            {scopesLoading && (
              <div className="w-full flex justify-center gap-2">
                <Spinner size="sm" />
                <span className="text-md text-default-500">Loading Scope of Work suggestions...</span>
              </div>
            )}
            {!scopesLoading && formData.scopeOfProject.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.scopeOfProject.map((scope, index) => (
                  <Chip
                    key={index}
                    variant="flat"
                    color="secondary"
                    size="lg"
                    onClose={() => {
                      setFormData((prev: any) => ({
                        ...prev,
                        scopeOfProject: prev.scopeOfProject.filter((item: string) => item !== scope),
                      }))
                    }}
                  >
                    {scope}
                  </Chip>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4 border-1 border-black rounded-xl p-5">
        <div className="flex flex-col">
          <span className={'flex flex-row text-md font-semibold items-center gap-2'}>
            What does your business offer for this project?
          </span>
          <span className={'text-sm'}>Briefly describe your product or service offering for this project.</span>
        </div>
        {/* Product/Service Description Textarea */}
        <div className="flex flex-col mt-4">
          <Textarea
            minRows={4}
            maxRows={6}
            variant="bordered"
            labelPlacement="outside"
            label="Product/Service offering"
            placeholder="Provide a brief description of the product or service offered"
            description="Summarize your product or service and its relevance to the project"
            value={productOffering}
            onChange={(e) => {
              setProductOffering(e.target.value)
              setFormData((prev) => ({ ...prev, productOffering: e.target.value }))
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                closeModal?.()
              }
            }}
          />
        </div>
      </div>

      {(isSalesProposal || isTenderProposal || isProjectProposal || isMarketingProposal) && (
        <div className="flex flex-col gap-4 border-1 border-black rounded-xl p-5">
          <div className="flex flex-col">
            <span className={'flex flex-row text-md font-semibold items-center gap-2'}>
              What information is specific to this document type?
            </span>
            <span className={'text-sm'}>Add details relevant to your selected document type to tailor the output.</span>
          </div>

          {/* Conditionally render Target Audience & Pricing for Sales/Marketing/Custom only */}
          {/* Updated logic: show if NOT Tender and NOT Project Proposal */}
          {isSalesProposal && (
            <>
              <div className="flex flex-col mt-4">
                <Select
                  label="Target Audience"
                  labelPlacement="outside"
                  variant="bordered"
                  placeholder="Select Target Audience"
                  selectedKeys={formData?.targetAudience ? new Set([formData?.targetAudience]) : new Set(['none'])}
                  onSelectionChange={(keys) => {
                    const value = Array.from(keys)[0]
                    setFormData((prev: any) => ({ ...prev, targetAudience: value }))
                  }}
                >
                  <SelectItem key="none">No specific audience</SelectItem>
                  <SelectItem key="IT department">IT department</SelectItem>
                  <SelectItem key="C-level executives">C-level executives</SelectItem>
                  <SelectItem key="Procurement">Procurement</SelectItem>
                  <SelectItem key="HR">HR</SelectItem>
                  <SelectItem key="Marketing">Marketing</SelectItem>
                  <SelectItem key="Other">Other</SelectItem>
                </Select>
              </div>

              {/* Pricing Options Select (NextUI) */}
              <div className="flex flex-col mt-4">
                <Select
                  label="Pricing Options"
                  labelPlacement="outside"
                  variant="bordered"
                  placeholder="Select Pricing Option"
                  selectedKeys={formData?.pricingOption ? new Set([formData.pricingOption]) : new Set()}
                  onSelectionChange={(keys) => {
                    const value = Array.from(keys)[0]
                    setFormData((prev: any) => ({ ...prev, pricingOption: value }))
                  }}
                >
                  <SelectItem key="Subscription (monthly, annually)">Subscription (monthly, annually)</SelectItem>
                  <SelectItem key="One-time purchase">One-time purchase</SelectItem>
                  <SelectItem key="Usage-based pricing">Usage-based pricing</SelectItem>
                  <SelectItem key="Custom package">Custom package</SelectItem>
                </Select>
              </div>
            </>
          )}

          {/* Tender Proposal: Render extra tender-specific fields */}
          {isTenderProposal && (
            <div className="flex flex-col gap-6 mt-6">
              {/* Project Scope: Summary, AI Suggestion, Tender Details, Upload */}
              <div className="flex flex-col gap-2 hidden">
                <label className="text-sm font-semibold">Project Scope</label>
                <Input
                  variant="bordered"
                  label="Summarize the project in one sentence."
                  placeholder="e.g. Supply and install 200 solar panels for public school"
                  value={formData.tenderProjectSummary ?? ''}
                  onChange={(e) => handleTenderInput('tenderProjectSummary', e.target.value)}
                  className="mb-2"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      closeModal?.()
                    }
                  }}
                />
                {/* AI Suggestion (simulate with a button, no actual AI call here) */}
                <Button
                  size="sm"
                  color="secondary"
                  variant="light"
                  className="w-fit mb-2"
                  onClick={() => {
                    // Simulate fetching suggestion from previous projects
                    handleTenderInput(
                      'tenderProjectSummary',
                      'AI suggestion: Supply and install 100 LED street lights in city center',
                    )
                  }}
                >
                  <Icon icon="lucide:sparkles" className="mr-1" /> AI Suggestion
                </Button>
                <Textarea
                  minRows={3}
                  variant="bordered"
                  label="Tender Details"
                  placeholder="Paste or summarize the tender details here"
                  value={formData.tenderDetails ?? ''}
                  onChange={(e) => handleTenderInput('tenderDetails', e.target.value)}
                  className="mb-2"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      closeModal?.()
                    }
                  }}
                />
                {/* Upload tender files */}
                <Button
                  variant="bordered"
                  color="default"
                  className="w-fit"
                  onClick={() => {
                    // Simulate file upload dialog (could use a dropzone in real app)
                    window.alert('File upload dialog (not implemented)')
                  }}
                >
                  <Icon icon="lucide:upload" className="mr-1" /> Upload files from tender
                </Button>
              </div>
              {/* Tender Reference Number */}
              <div className="flex flex-col gap-1">
                <Input
                  label="Tender Reference Number"
                  labelPlacement="outside"
                  variant="bordered"
                  placeholder="e.g. GOV-2024-1234"
                  value={formData.tenderReferenceNumber ?? ''}
                  onChange={(e) => handleTenderInput('tenderReferenceNumber', e.target.value)}
                  className="mb-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      closeModal?.()
                    }
                  }}
                />
              </div>
              <div className="flex gap-2">
                <div className="flex w-full gap-3">
                  {/* Issuing Authority */}
                  <Select
                    label="Issuing Authority"
                    labelPlacement="outside"
                    variant="bordered"
                    placeholder="Select Issuing Authority"
                    selectedKeys={formData.issuingAuthority ? new Set([formData.issuingAuthority]) : new Set(['none'])}
                    onSelectionChange={(keys) => {
                      const value = Array.from(keys)[0]
                      handleTenderInput('issuingAuthority', value)
                    }}
                  >
                    <SelectItem key="none">No specific authority</SelectItem>
                    {issuingAuthorities.map((auth) => (
                      <SelectItem key={auth}>{auth}</SelectItem>
                    ))}
                  </Select>

                  {/* Tender Submission Format */}
                  <Select
                    label="Tender Submission Format"
                    labelPlacement="outside"
                    variant="bordered"
                    placeholder="Select Format"
                    selectedKeys={
                      formData.tenderSubmissionFormat ? new Set([formData.tenderSubmissionFormat]) : new Set(['none'])
                    }
                    onSelectionChange={(keys) => {
                      const value = Array.from(keys)[0]
                      handleTenderInput('tenderSubmissionFormat', value)
                    }}
                  >
                    <SelectItem key="none">No specific format</SelectItem>
                    {tenderSubmissionFormats.map((fmt) => (
                      <SelectItem key={fmt}>{fmt}</SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
              {/* Compliance Requirements */}
              <div className="flex flex-col gap-1">
                <Textarea
                  label="Compliance Requirements"
                  labelPlacement="outside"
                  minRows={3}
                  variant="bordered"
                  placeholder="e.g. Must comply with ISO 9001, provide safety certificates, ..."
                  description="Paste compliance requirements here"
                  value={formData.complianceRequirements ?? ''}
                  onChange={(e) => handleTenderInput('complianceRequirements', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      closeModal?.()
                    }
                  }}
                />
              </div>
              {/* Cost Breakdown */}
              <div className="flex flex-col gap-1">
                <Textarea
                  label="Cost Breakdown"
                  labelPlacement="outside"
                  placeholder="e.g. Materials: $50,000; Labor: $30,000; Overheads: $10,000'"
                  description="Enter cost breakdown (e.g. line items, manual adjustments)"
                  minRows={3}
                  variant="bordered"
                  value={formData.costBreakdown ?? ''}
                  onChange={(e) => handleTenderInput('costBreakdown', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      closeModal?.()
                    }
                  }}
                />
              </div>
              {/* Risk Management */}
              <div className="flex flex-col gap-1">
                <Textarea
                  label="Risk Management"
                  labelPlacement="outside"
                  minRows={3}
                  variant="bordered"
                  placeholder="Common risks include supply chain delays, labor shortages, regulatory changes."
                  description="Customize risk management plan"
                  value={formData.riskManagement ?? ''}
                  onChange={(e) => handleTenderInput('riskManagement', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      closeModal?.()
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Updated logic: show if Project Proposal */}
          {isProjectProposal && (
            <>
              <div className="flex flex-col mt-4">
                <Select
                  label="Project Phases"
                  labelPlacement="outside"
                  placeholder="Select Project Phases"
                  description="Select none, one or more phases that match your project"
                  variant="bordered"
                  selectionMode="multiple"
                  selectedKeys={formData?.projectPhases ? new Set(formData.projectPhases) : new Set()}
                  onSelectionChange={(keys) => {
                    const values = Array.from(keys)
                    setFormData((prev: any) => ({ ...prev, projectPhases: values }))
                  }}
                >
                  {(formData?.availablePhases || ['Discovery', 'Design', 'Development', 'Testing', 'Deployment']).map(
                    (phase: string) => (
                      <SelectItem key={phase}>{phase}</SelectItem>
                    ),
                  )}
                </Select>
                <Button
                  variant="light"
                  size="sm"
                  onPress={() => {
                    const newPhase = prompt('Enter a new phase:')
                    if (newPhase) {
                      setFormData((prev: any) => {
                        const updatedPhases = [...(prev.projectPhases || []), newPhase]
                        const updatedAvailablePhases = Array.from(
                          new Set([
                            ...(prev.availablePhases || [
                              'Discovery',
                              'Design',
                              'Development',
                              'Testing',
                              'Deployment',
                            ]),
                            newPhase,
                          ]),
                        )
                        return {
                          ...prev,
                          projectPhases: updatedPhases,
                          availablePhases: updatedAvailablePhases,
                        }
                      })
                    }
                  }}
                >
                  Add Custom Phase
                </Button>
              </div>
              <div className="flex flex-col mt-4">
                <Textarea
                  label="Budget Estimation"
                  labelPlacement="outside"
                  minRows={3}
                  variant="bordered"
                  placeholder="$50,000 for design, $100,000 for development, $20,000 for testing."
                  description="Resource allocation, software licensing, maintenance..."
                  value={formData.budgetEstimation ?? ''}
                  onChange={(e) => setFormData((prev: any) => ({ ...prev, budgetEstimation: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      closeModal?.()
                    }
                  }}
                />
              </div>
              {/* <Button
                  variant="light"
                  size="sm"
                  onPress={() => {
                    const estimatedBudget = 'AI Calculation: $50,000 for design, $100,000 for development, $20,000 for testing.';
                    setFormData((prev: any) => ({ ...prev, budgetEstimation: estimatedBudget }));
                  }}
                >
                  Estimate Budget
                </Button> */}
            </>
          )}

          {/* Marketing Proposal: Render extra marketing-specific fields */}
          {isMarketingProposal && (
            <>
              <div className="flex flex-col mt-4">
                <Textarea
                  label="Campaign Objective"
                  labelPlacement="outside"
                  minRows={3}
                  variant="bordered"
                  placeholder="e.g., Increase brand awareness among Gen Z audience."
                  description="Define the primary goal of your marketing campaign, such as boosting visibility or driving engagement"
                  value={formData.campaignObjective ?? ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, campaignObjective: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      closeModal?.()
                    }
                  }}
                />
              </div>
              <div className="flex flex-col mt-4">
                <Select
                  label="Target Channels"
                  labelPlacement="outside"
                  variant="bordered"
                  selectionMode="multiple"
                  placeholder="Select Target Channels"
                  description="Select one or more channels that match your project"
                  selectedKeys={formData.targetChannels ? new Set(formData.targetChannels) : new Set()}
                  onSelectionChange={(keys) => {
                    const values = Array.from(keys)
                    setFormData((prev) => ({ ...prev, targetChannels: values }))
                  }}
                >
                  {['Social Media', 'Email', 'Search Engines', 'Print Media', 'TV', 'Radio'].map((channel) => (
                    <SelectItem key={channel}>{channel}</SelectItem>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col mt-4">
                <Textarea
                  label="Key Performance Indicators (KPIs)"
                  labelPlacement="outside"
                  minRows={3}
                  variant="bordered"
                  placeholder="e.g., Social media engagement, email open rate, conversion rate."
                  description="Define the measurable metrics that will determine the success of your marketing campaign"
                  value={formData.kpis ?? ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, kpis: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      closeModal?.()
                    }
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ------------------ MAIN COMPONENT: CreateNewDocument ------------------ //
export default function DocWizard(props: any) {
  const { isOpen, onClose, projectUUID, activeClient, showSubscriptionError } = props
  const [formData, setFormData] = useState<any>({
    projectUUID,
    documentType: '',
    // paymentTerms: '',
    // documentLength: '',
    scopeOfProject: [],
    files: [],
    filesToUpload: [],
    // Tender Proposal specific fields
    // tenderReferenceNumber: '',
    // issuingAuthority: '',
    // tenderSubmissionFormat: '',
    // complianceRequirements: '',
    // costBreakdown: '',
    // riskManagement: '',
    // tenderProjectSummary: '',
    // tenderDetails: '',
    // Marketing Proposal specific fields
    // campaignObjective: '',
    // targetChannels: [],
    // kpis: '',
  })

  const [documentData, setDocumentData] = useState({})

  const updateDocumentData = (key: string, value: any) => {
    setDocumentData((prevData) => ({ ...prevData, [key]: value }))
  }

  // New error state for input validation in Step 2
  const [errors, setErrors] = useState({ title: false, clientName: false, description: false })

  const [scopes, setScopes] = useState<string[]>([])
  const [scopesLoading, setScopesLoading] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [showAdvancedOption, setShowAdvancedOption] = useState<boolean>(false)
  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false)
  const [outOfContextError, setOutOfContextError] = useState<string | null>(null)

  const [fineTuneText, setFineTuneText] = useState<string>('')
  const [defaultDocLength, setDefaultDocLength] = useState<string>('Precise')
  const [formality, setFormality] = useState<string>('Semi-formal')
  const [modeOfAddress, setModeOfAddress] = useState<string>('Second-person tone')
  const [audianceType, setAudianceType] = useState<string>('General readers')
  const [language, setLanguage] = useState<string>('English')
  const [productOffering, setProductOffering] = useState<string>('')

  const { clientUUID } = useParams()
  const { io } = useSocketContextData()
  const { getUserSubscription } = useSubscriptionContext()
  const { templates, loadTemplates, loadMyClient, activeWorkspace, clientsFullList, workspace } =
    useWorkspaceContextData()
  const navigate = useNavigate()

  const [openAccordion, setOpenAccordion] = useState(1)

  const [selectedClient, setSelectedClient] = useState<{ value: string; label: string } | null>(null)
  useEffect(() => {
    if (activeClient) {
      setSelectedClient({ value: activeClient.uuid, label: activeClient.name })
      setFormData((prev) => ({ ...prev, clientName: activeClient.name }))
    }
  }, [activeClient])
  useEffect(() => {
    // For external close calls
    // @ts-ignore
    window.closeCreateModal = onClose
  }, [onClose])
  useEffect(() => {
    if (workspace) {
      setFineTuneText(workspace.fineTuneText ?? '')
      setAudianceType(workspace.audianceType ?? audianceType)
      setDefaultDocLength(workspace.defaultDocLength ?? defaultDocLength)
      setFormality(workspace.formality ?? formality)
      setModeOfAddress(workspace.modeOfAddress ?? modeOfAddress)
      setLanguage(workspace.language ?? language)
      setProductOffering(workspace.productOffering ?? '')
    }
  }, [workspace])

  const loadScope = async (description: string) => {
    if (description && io) {
      setScopesLoading(true)
      // Clear any previous out of context error
      setOutOfContextError(null)
      try {
        const projectData = description + ' \n' + formData.filesDescription
        const response = await io.emitWithAck('getScopeItemsOfProject', { description: projectData, documentType: formData.documentType })

        if (response) {
          try {
            // Attempt to parse the response as JSON
            const parsedResponse = JSON.parse(response)

            // Check for OUT_OF_CONTEXT error from server
            if (parsedResponse.error === 'OUT_OF_CONTEXT') {
              console.warn('OUT_OF_CONTEXT error:', parsedResponse.message)
              setOutOfContextError(
                parsedResponse.message ||
                  'The project description appears to be out of context. Please provide a more specific business-related description.',
              )
              setScopes([])
              setFormData((prev: any) => ({ ...prev, scopeOfProject: [] }))
              setScopesLoading(false)
              return
            }

            if (Array.isArray(parsedResponse['Scope of work'])) {
              const scopesArray = parsedResponse['Scope of work']
              if (scopesArray.length > 0) {
                setScopes(scopesArray)
                setFormData((prev: any) => ({
                  ...prev,
                  scopeOfProject: scopesArray,
                }))
              } else {
                console.error('Response does not contain valid scope items')
                setScopes([])
                setFormData((prev: any) => ({ ...prev, scopeOfProject: [] }))
              }
            } else {
              console.error('Invalid response format: expected array')
              setScopes([])
              setFormData((prev: any) => ({ ...prev, scopeOfProject: [] }))
            }
          } catch (error) {
            console.error('Error parsing response as JSON:', error)
            setScopes([])
            setFormData((prev: any) => ({ ...prev, scopeOfProject: [] }))
          }
        }
      } catch (err) {
        console.error('Error during API call:', err)
        setScopes([])
        setFormData((prev: any) => ({ ...prev, scopeOfProject: [] }))
      }
      setScopesLoading(false)
    }
  }

  // Helper: Process extracted document data (from file AI extraction)
  const handleExtractedDocumentData = (response: any) => {
    // Debug log for the full response object
    console.log('Extracted document data:', response)

    // Set formData fields, using optional chaining and defaulting to "" if not present
    setFormData((prev: any) => ({
      ...prev,
      projectDescription: response?.['Project description'] ?? '',
      scopeOfProject: Array.isArray(response?.['Scope of work']) ? response['Scope of work'] : [],
      clientName: response?.['Client name'] ?? '',
      clientWebsite: response?.['Client website'] ?? '',
    }))

    // Set the scopes directly from the response
    setScopes(Array.isArray(response?.['Scope of work']) ? response['Scope of work'] : [])

    // Update language selection in AI Settings based on "Primary document language"
    if (
      response?.['Primary document language'] &&
      ['English', 'English (UK)', 'Spanish', 'French', 'German', 'Arabic', 'Japanese'].includes(
        response['Primary document language'],
      )
    ) {
      setLanguage(response['Primary document language'])
    } else {
      setLanguage('English')
    }

    // Call updateDocumentData to reflect the entire response object
    updateDocumentData('extractedDocumentData', response)
  }

  const handleGenerate = async () => {
    setLoading(true)
    try {
      const res = await io.emitWithAck(PowerDocsEventsName.CREATE_DOCUMENT, {
        ...formData,
        template: formData?.template?.id,
        workspaceUUID: activeWorkspace,
        projectUUID,
        scope: (formData.scopeOfProject || []).join('\n'),
        token: localStorage.getItem('accessToken'),
        formality,
        modeOfAddress,
        defaultDocLength,
        fineTuneText,
        audianceType,
        language,
        filesToUpload: [],
        formData,
      })
      if (res?.error) {
        if (res.error === 'OUT_OF_CONTEXT') {
          setOutOfContextError(
            res.message ||
              'Project description appears out of context. Please be more specific or adjust the description.',
          )
          setFormData((prev) => ({ ...prev, scopeOfProject: [] }))
          setScopes([])
          setLoading(false)
          setOpenAccordion(3)
          return
        }
        console.log('Error raised')
        setLoading(false)
        return
      }
    } catch (error) {
      Mixpanel.track('Document creation error', {
        error_message: error,
      })
      setLoading(false)
    }
  }

  const reset = () => {
    setFormData({
      projectUUID,
      documentType: '',
      // paymentTerms: '',
      // documentLength: '',
      scopeOfProject: [],
      files: [],
      filesToUpload: [],
      // Tender Proposal specific fields
      // tenderReferenceNumber: '',
      // issuingAuthority: '',
      // tenderSubmissionFormat: '',
      // complianceRequirements: '',
      // costBreakdown: '',
      // riskManagement: '',
      // tenderProjectSummary: '',
      // tenderDetails: '',
      // Marketing Proposal specific fields
      // campaignObjective: '',
      // targetChannels: [],
      // kpis: '',
    })
    setSelectedClient(null)
    setOpenAccordion(1)
  }

  useEffect(() => {
    if (io) {
      const handleRedirect = (documentType: string, documentUUID: string) => {
        if (window.location.pathname.startsWith('/admin/pipeline')) return
        let link = `/client/${formData.clientName}/project/${documentUUID}/proposal`
        switch (documentType) {
          case 'QUOTATION':
            link = `/client/${formData.clientName}/project/${documentUUID}/quotation`
            break
          case 'OFFER':
            link = `/client/${formData.clientName}/project/${documentUUID}/offer`
            break
          default:
            link = `/client/${formData.clientName}/project/${documentUUID}/proposal`
        }
        setTimeout(() => {
          setLoading(false)
          navigate(link)
        }, 500)
      }
      const proposalGeneratedEvent = (res) => {
        loadMyClient?.(activeWorkspace)
        if (res.error) {
          showSubscriptionError?.()
        } else {
          getUserSubscription?.()
          reset()
          onClose()
          handleRedirect('PROPOSAL', res.uuid)

          Mixpanel.track('Document wizard step completed', {
            step_number: 3,
            step_name: 'Budget & terms',
            document_type: formData.documentType,
          })

          Mixpanel.track('Document created', {
            document_id: res.uuid,
            document_type: formData.documentType,
          })
        }
      }
      const quotationGeneratedEvent = (res) => {
        loadMyClient?.(activeWorkspace)
        if (res.error) {
          showSubscriptionError?.()
        } else {
          getUserSubscription?.()
          reset()
          onClose()
          handleRedirect('QUOTATION', res.uuid)

          Mixpanel.track('Document wizard step completed', {
            step_number: 3,
            step_name: 'Budget & terms',
            document_type: formData.documentType,
          })

          Mixpanel.track('Document created', {
            document_id: res.uuid,
            document_type: formData.documentType,
          })
        }
      }
      const offerGeneratedEvent = (res) => {
        loadMyClient?.(activeWorkspace)
        if (res.error) {
          showSubscriptionError?.()
        } else {
          getUserSubscription?.()
          reset()
          onClose()
          handleRedirect('OFFER', res.uuid)
          Mixpanel.track('Document wizard step completed', {
            step_number: 3,
            step_name: 'Budget & terms',
            document_type: formData.documentType,
          })

          Mixpanel.track('Document created', {
            document_id: res.uuid,
            document_type: formData.documentType,
          })
        }
      }
      const powerDocGenerated = (res) => {
        emitter.emit('updateSubmissionCount', { step: 1, uuid: activeWorkspace })
      }
      io.on(PowerDocsEventsName.PROPOSAL_GENERATED, proposalGeneratedEvent)
      io.on(PowerDocsEventsName.QUOTATION_GENERATED, quotationGeneratedEvent)
      io.on(PowerDocsEventsName.OFFER_GENERATED, offerGeneratedEvent)
      io.on(PowerDocsEventsName.POWERDOC_CREATED, powerDocGenerated)

      return () => {
        io.off(PowerDocsEventsName.PROPOSAL_GENERATED, proposalGeneratedEvent)
        io.off(PowerDocsEventsName.QUOTATION_GENERATED, quotationGeneratedEvent)
        io.off(PowerDocsEventsName.OFFER_GENERATED, offerGeneratedEvent)
        io.off(PowerDocsEventsName.POWERDOC_CREATED, powerDocGenerated)
      }
    }
  }, [io, formData])

  // State for enabling "Next" button in Step 2
  const [isStep2NextEnabled, setIsStep2NextEnabled] = useState<boolean>(false)

  useEffect(() => {
    const isTitleValid = formData?.title?.trim().length > 0
    const isClientNameValid = formData?.clientName?.trim().length > 0

    setErrors({
      title: !isTitleValid,
      clientName: !isClientNameValid,
    })

    setIsStep2NextEnabled(isTitleValid && isClientNameValid)
  }, [formData?.title, formData?.clientName, setIsStep2NextEnabled])

  // State for enabling "Generate" button in Step 3
  const [isStep3NextEnabled, setIsStep3NextEnabled] = useState<boolean>(false)

  useEffect(() => {
    const isDescriptionValid = formData?.projectDescription?.trim().length > 0

    setErrors({
      description: !isDescriptionValid || !!outOfContextError,
    })

    setIsStep3NextEnabled(isDescriptionValid && !outOfContextError)
  }, [formData?.projectDescription, outOfContextError, setIsStep3NextEnabled])

  // Templating
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null)
  const applyTemplate = () => {
    if (selectedTemplate) {
      const template = templates.find((item) => item.id === selectedTemplate)
      if (template) {
        setFormData((prev: any) => ({
          ...prev,
          documentType: template.variant.toUpperCase(),
          subType: template.subType ?? '',
          template,
          formality: template.powerDoc?.formality ?? 'Semi-formal',
          fineTuneText: template.powerdoc?.fineTuneText ?? '',
          defaultDocLength: template.powerDoc?.defaultDocLength ?? 'Precise',
          modeOfAddress: template.powerDoc?.modeOfAddress ?? 'Second-person tone',
          audianceType: template.powerDoc?.audianceType ?? 'General readers',
          language: template.powerDoc?.language ?? 'English',
        }))
        setFormality(template.powerDoc?.formality ?? 'Semi-formal')
        setFineTuneText(template.powerdoc?.fineTuneText ?? '')
        setDefaultDocLength(template.powerDoc?.defaultDocLength ?? 'Precise')
        setModeOfAddress(template.powerDoc?.modeOfAddress ?? 'Second-person tone')
        setAudianceType(template.powerDoc?.audianceType ?? 'General readers')
        setLanguage(template.powerDoc?.language ?? 'English')
        setOpenAccordion(1)
      }
    }
  }
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
  
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [])
  return (
    <>
      {/* Advanced Settings Modal */}
      <Modal
        isOpen={isAdvancedSettingsOpen}
        onClose={() => setIsAdvancedSettingsOpen(false)}
        scrollBehavior="inside"
        size="lg"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">Powerdocs AI Settings</ModalHeader>
          <ModalBody>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-1">
                <span className="flex items-center gap-2">
                  Document length <Icon icon="lucide:circle-help" />
                </span>
                <div className="p-1 rounded-xl w-fit bg-default-100">
                  {['Concise', 'Precise', 'Detailed'].map((item) => (
                    <Button
                      key={item}
                      size="sm"
                      className={defaultDocLength === item ? activeButtonClass : inActiveButtonClass}
                      onPress={() => {
                        setDefaultDocLength(item)
                        setFormData((prev: any) => ({
                          ...prev,
                          defaultDocLength: item,
                        }))
                      }}
                    >
                      {item}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className={'flex flex-row items-center gap-2 text-sm'}>
                  Document language
                  <Tooltip content="Select the document language" color="foreground">
                    <Icon icon={'lucide:circle-help'} />
                  </Tooltip>
                </span>
                <div className={'p-1 rounded-xl w-full max-w-xs bg-white'}>
                  <Select
                    label="Language"
                    labelPlacement="outside"
                    variant="bordered"
                    placeholder="Select Language"
                    selectedKeys={new Set([language])}
                    onSelectionChange={(keys) => {
                      const selectedLang = Array.from(keys)[0]
                      setLanguage(selectedLang)
                      setFormData((prev: any) => ({
                        ...prev,
                        language: selectedLang,
                      }))
                    }}
                  >
                    {['English', 'English (UK)', 'Spanish', 'French', 'German', 'Arabic', 'Japanese'].map((lang) => (
                      <SelectItem key={lang}>{lang}</SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="flex items-center gap-2">
                  Formality <Icon icon="lucide:circle-help" />
                </span>
                <div className="p-1 rounded-xl w-fit bg-default-100">
                  {['Informal', 'Semi-formal', 'Professional'].map((item) => (
                    <Button
                      key={item}
                      size="sm"
                      className={formality === item ? activeButtonClass : inActiveButtonClass}
                      onPress={() => {
                        setFormality(item)
                        setFormData((prev: any) => ({
                          ...prev,
                          formality: item,
                        }))
                      }}
                    >
                      {item}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="flex items-center gap-2">
                  Mode of address <Icon icon="lucide:circle-help" />
                </span>
                <div className="p-1 rounded-xl w-fit bg-default-100">
                  {['First-person tone', 'Second-person tone'].map((item) => (
                    <Button
                      key={item}
                      size="sm"
                      className={modeOfAddress === item ? activeButtonClass : inActiveButtonClass}
                      onPress={() => {
                        setModeOfAddress(item)
                        setFormData((prev: any) => ({
                          ...prev,
                          modeOfAddress: item,
                        }))
                      }}
                    >
                      {item}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="flex items-center gap-2">
                  Audience type <Icon icon="lucide:circle-help" />
                </span>
                <div className="p-1 rounded-xl w-fit bg-default-100">
                  {['Experts', 'General readers', 'Technical users'].map((item) => (
                    <Button
                      key={item}
                      size="sm"
                      className={audianceType === item ? activeButtonClass : inActiveButtonClass}
                      onPress={() => {
                        setAudianceType(item)
                        setFormData((prev: any) => ({
                          ...prev,
                          audianceType: item,
                        }))
                      }}
                    >
                      {item}
                    </Button>
                  ))}
                </div>
              </div>
              <Textarea
                label={
                  <span className="flex flex-row items-center gap-2">
                    Text input for fine-tuning <Icon icon="lucide:circle-help" />
                  </span>
                }
                placeholder="Enter any additional requirements or stylistic preferences..."
                labelPlacement="outside"
                value={fineTuneText}
                onChange={(e) => {
                  setFineTuneText(e.target.value)
                  setFormData((prev: any) => ({
                    ...prev,
                    fineTuneText: e.target.value,
                  }))
                }}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setIsAdvancedSettingsOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Main Modal */}
      <Modal
        className="no-scrollbar"
        backdrop="blur"
        isOpen={isOpen}
        onClose={onClose}
        scrollBehavior="inside"
        size="full"
        isDismissable={false}
      >
        <div
          onClick={(e) => {
            if (e.target.getAttribute('data-slot') === 'wrapper') {
              reset()
              onClose()
            }
          }}
        >
          <ModalContent className="p-5 max-w-[1500px]">
            {(onClose) => (
              <>
                <ModalHeader className="flex flex-col gap-4">
                  {/* <div className="absolute top-0 right-0 w-[500px]">
                    <pre className="text-xs overflow-auto bg-gray-100 p-2 rounded mt-2">
                        {JSON.stringify(formData, null, 2)}
                        {isStep3NextEnabled ? 'True' : 'False'}
                    </pre>
                  </div> */}
                  <div className="flex w-full justify-between px-2 items-center">
                    <div className="flex w-1/2 items-center gap-2">
                      <div className="text-xl font-semibold">Create new business document</div>
                      <Button
                        color="default"
                        variant="light"
                        onPress={() => {
                          reset()
                          setOpenAccordion(1)
                          setErrors({ title: false, clientName: false, description: false })
                          // setSelectedClient(null);
                          setDocumentData({})
                          setScopes([])
                          setScopesLoading(false)
                          setProductOffering('')
                        }}
                      >
                        Reset
                      </Button>
                    </div>
                    <div className="flex w-1/2 justify-end gap-2">
                      <Button
                        className="w-fit"
                        variant="flat"
                        size="md"
                        onPress={() => setIsAdvancedSettingsOpen(true)}
                        startContent={<Icon icon="lucide:cog" />}
                      >
                        AI settings
                      </Button>
                      <Select
                        variant="bordered"
                        className="max-w-xs"
                        placeholder="Select a template…"
                        size="md"
                        listboxProps={{
                          emptyContent: (
                            <div className="flex flex-col gap-2">
                              <span>No templates available. Define a document as a template first.</span>
                              <i>Document editor → Save as template</i>
                            </div>
                          ),
                        }}
                        onChange={(e) => setSelectedTemplate(e.target.value ? parseInt(e.target.value, 10) : null)}
                      >
                        {templates.map((template) => (
                          <SelectItem
                            key={template.id}
                            renderEmpty={() => (
                              <div className="text-center text-default-400 py-2">No clients found</div>
                            )}
                            value={template.id}
                            title={`${template.variant} | ${template.title}`}
                            onSelect={console.log}
                          >
                            {template.variant} | {template.title}
                          </SelectItem>
                        ))}
                      </Select>
                      <Button
                        className="w-fit"
                        variant="flat"
                        color="default"
                        size="md"
                        isDisabled={!selectedTemplate}
                        onPress={applyTemplate}
                      >
                        Select
                      </Button>
                    </div>
                  </div>
                </ModalHeader>
                <ModalBody className="scrollbar-hide">
                  <Accordion
                    selectedKeys={[String(openAccordion)]}
                    variant="splitted"
                    itemClasses={{ title: 'font-semibold', base: 'px-7 py-4' }}
                  >
                    <AccordionItem
                      key="1"
                      aria-label="Project Type"
                      title="1. Project Type"
                      subtitle={<span>Select the document type suitable for your business need</span>}
                    >
                      <StepDocumentType
                        formData={formData}
                        setFormData={setFormData}
                        setOpenAccordion={setOpenAccordion}
                        isVisible={openAccordion === 1}
                        closeModal={onClose}
                      />
                    </AccordionItem>
                    <AccordionItem
                      key="2"
                      aria-label="Project Information"
                      title="2. Project Information"
                      subtitle={<span>Provide a short brief including the project goals and requirements</span>}
                    >
                      <StepProjectInfo
                        formData={formData}
                        setFormData={setFormData}
                        clientsFullList={clientsFullList}
                        selectedClient={selectedClient}
                        setSelectedClient={setSelectedClient}
                        loadScope={loadScope}
                        errors={errors}
                        setNextButtonStatus={setIsStep2NextEnabled}
                        updateDocumentData={updateDocumentData}
                        setLanguage={setLanguage}
                        isVisible={openAccordion === 2}
                        closeModal={onClose}
                      />
                    </AccordionItem>
                    <AccordionItem
                      key="3"
                      aria-label="Project Scope"
                      title="3. Project Scope"
                      subtitle={
                        <span>
                          Review the project scope and ensure it aligns with your company's expertise and offering
                        </span>
                      }
                    >
                      <StepProjectScope
                        formData={formData}
                        setFormData={setFormData}
                        scopes={scopes}
                        scopesLoading={scopesLoading}
                        showAdvancedOption={showAdvancedOption}
                        setShowAdvancedOption={setShowAdvancedOption}
                        defaultDocLength={defaultDocLength}
                        setDefaultDocLength={setDefaultDocLength}
                        formality={formality}
                        setFormality={setFormality}
                        modeOfAddress={modeOfAddress}
                        setModeOfAddress={setModeOfAddress}
                        audianceType={audianceType}
                        setAudianceType={setAudianceType}
                        language={language}
                        setLanguage={setLanguage}
                        fineTuneText={fineTuneText}
                        setFineTuneText={setFineTuneText}
                        loading={loading}
                        setIsAdvancedSettingsOpen={setIsAdvancedSettingsOpen}
                        loadScope={loadScope}
                        productOffering={productOffering}
                        setProductOffering={setProductOffering}
                        updateDocumentData={updateDocumentData}
                        setNextButtonStatus={setIsStep3NextEnabled}
                        outOfContextError={outOfContextError}
                        setOutOfContextError={setOutOfContextError}
                        isVisible={openAccordion === 3}
                        closeModal={onClose}
                      />
                    </AccordionItem>
                  </Accordion>
                </ModalBody>
                <ModalFooter className="bg-sky-500/[.06]">
                  <div className="flex w-full justify-end gap-2">
                    {openAccordion > 1 && (
                      <Button variant="bordered" color="default" onPress={() => setOpenAccordion(openAccordion - 1)}>
                        Back
                      </Button>
                    )}
                    {openAccordion === 1 && (
                      <Button
                        color="secondary"
                        onPress={() => setOpenAccordion(openAccordion + 1)}
                        isDisabled={loading || !formData.documentType}
                        endContent={<Icon icon="lucide:chevron-right" />}
                      >
                        Next step
                      </Button>
                    )}
                    {openAccordion === 2 && (
                      <Button
                        color="secondary"
                        onPress={() => setOpenAccordion(openAccordion + 1)}
                        isDisabled={loading || !isStep2NextEnabled}
                        endContent={<Icon icon="lucide:chevron-right" />}
                      >
                        Next step
                      </Button>
                    )}
                    {openAccordion === 3 && (
                      <Button
                        isDisabled={loading || !isStep3NextEnabled}
                        color="secondary"
                        onClick={handleGenerate}
                        startContent={<Icon icon="lucide:sparkles" />}
                        isLoading={loading}
                      >
                        Generate Document
                      </Button>
                    )}
                  </div>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </div>
        {loading && isOpen && (
          <div className="absolute w-full h-full top-0 left-0" style={{ zIndex: 1000 }}>
            <div className="bg-black/75 backdrop-blur-sm w-full h-full flex flex-col justify-center items-center gap-2">
              <div className="flex flex-row gap-2 justify-center items-center">
                <Spinner size="lg" color="secondary" />
                <span className="text-white text-xl">Processing information…</span>
              </div>
              <span className="text-white text-sm opacity-60 text-center">
                Please wait and keep this browser tab open
              </span>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}
